import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { css } from '@styled-system/css'

import { axios } from 'api'
import { useUserContext } from 'context'
import { useModal_deprecated } from 'hooks'
import useToast from 'hooks/useAlertAndToast/useToast'
import { Flex } from 'ui/bend'

import EventRegistrationModal from './EventRegistrationModal'
import { useLocation } from 'react-router-dom'
import CancelConfirmationModal from './CancelConfirmationModal'
import {
  community_event_rsvp_click_event,
  community_event_rsvp_success_event,
  community_event_sign_in_to_register_click_event,
  community_event_cancel_click_event,
  community_event_cancel_success_event
} from 'gtm/pushEvents'
import moment from 'moment'
const REGISTRATION_CLOSED = 'REGISTRATION CLOSED'
const COMING_SOON = 'COMING SOON'
const disableButton = (str) => [REGISTRATION_CLOSED, COMING_SOON].includes(str)

const Button = styled.button`
  border-radius: 360px;
  background: #fff;
  color: #000;
  text-align: center;
  font-family: 'Proxima-Nova';
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  padding: 10px 20px;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  border: 0px;
  min-width: 115px;
`

const ActionGroup = styled(Flex)(({ $compact, $fullWidth }) =>
  css({
    flexDirection:
      $compact && $fullWidth ? 'column' : $compact ? 'row' : ['column', 'row'],
    flexWrap: 'wrap',
    gap: $compact ? '8px' : '12px',
    width:
      $compact && $fullWidth ? '100%' : $compact ? 'auto' : ['100%', 'auto']
  })
)

const getButtonProps = (size = 'default', fullWidth = false) => {
  if (size === 'compact') {
    return {
      $compact: true,
      width: fullWidth ? '100%' : 'auto'
    }
  }

  return {
    width: ['100%', 'auto'],
    minWidth: '140px'
  }
}

const STATUS_LABELS = {
  registered: 'Registered',
  waitlisted: 'Waitlisted',
  pending: 'Pending approval',
  declined: 'Registration declined'
}

const EventRegisterButton = ({
  eventId,
  eventTitle,
  registrationQuestions = [],
  registrationDefaults = {},
  registrationStatus: initialStatus = 'none',
  isPast = false,
  size = 'default',
  fullWidth = false,
  requireApproval = false,
  waitlistStatus = false,
  salesStart = false,
  salesEnd = false,
  spotsRemaining = null,
  usedSlots,
  showCancelButton = false,
  onRegistrationChange
}) => {
  const buttonProps = getButtonProps(size, fullWidth)
  const isCompact = size === 'compact'
  const { user } = useUserContext()
  const location = useLocation()
  const pathname = location.pathname
  // GA `location` (surface): card RSVPs fire on the listing (/events); detail
  // and related-carousel RSVPs fire on the EDP (/events/:id).
  const gaLocation = /^\/events\/[^/]+/.test(pathname)
    ? 'event_detail'
    : 'listing'
  const gaEvent = { id: eventId, title: eventTitle }
  const maxSlots = spotsRemaining === null ? 0 : spotsRemaining + usedSlots
  const isFull = Number(maxSlots) > 0 && Number(usedSlots) >= Number(maxSlots)
  const registrationModal = useModal_deprecated()
  const setToast = useToast()
  const [registrationStatus, setRegistrationStatus] = useState(initialStatus)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [openConfirmation, setOpenConfirmation] = useState(false)

  useEffect(() => {
    setRegistrationStatus(initialStatus)
  }, [initialStatus])

  const questions = useMemo(
    () => (Array.isArray(registrationQuestions) ? registrationQuestions : []),
    [registrationQuestions]
  )
  const mainButtonText = useMemo(() => {
    const now = moment()
    const max = Number(maxSlots)
    const used = Number(usedSlots)

    if (salesStart && now.isBefore(salesStart)) {
      return COMING_SOON
    }

    if (salesEnd && now.isAfter(salesEnd)) {
      return REGISTRATION_CLOSED
    }

    if (max > 0 && used >= max) {
      return waitlistStatus ? 'JOIN WAITLIST' : REGISTRATION_CLOSED
    }

    return requireApproval ? 'REQUEST TO JOIN' : 'RSVP'
  }, [
    salesStart,
    salesEnd,
    maxSlots,
    usedSlots,
    waitlistStatus,
    requireApproval
  ])

  if (!eventId) {
    return (
      <Button variant='solid' disabled {...buttonProps} className='rsvp'>
        RSVP
      </Button>
    )
  }

  if (isPast) {
    return (
      <Button variant='solid' disabled {...buttonProps} className='rsvp'>
        Event ended
      </Button>
    )
  }

  const isRegistered =
    registrationStatus !== 'none' && registrationStatus !== 'declined'

  const handleRegistrationSuccess = (status) => {
    // Push the GA event first: setRegistrationStatus / onRegistrationChange both
    // trigger synchronous re-renders (React 16 doesn't batch post-await updates),
    // and onRegistrationChange refetches the event(s). If any of that throws, the
    // exception unwinds into postRegistration's try/catch and the dataLayer push
    // would be silently skipped. Emitting up front guarantees the event fires.
    community_event_rsvp_success_event({ event: gaEvent, resultStatus: status })
    setRegistrationStatus(status)
    onRegistrationChange?.(status)
  }

  const getCancelConfirmation = (e) => {
    e.preventDefault()
    e.stopPropagation()
    community_event_cancel_click_event({
      event: gaEvent,
      location: gaLocation,
      registrationStatus
    })
    if (registrationStatus === 'registered') {
      setOpenConfirmation(true)
    } else {
      handleCancel()
    }
  }

  const handleRegisterClick = async (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!user) {
      community_event_sign_in_to_register_click_event({
        event: gaEvent,
        location: gaLocation
      })
      window.location.href = `/signin?redirect_to=${pathname}`
      return
    }

    if (isRegistered) return

    community_event_rsvp_click_event({
      event: gaEvent,
      location: gaLocation,
      registrationStatus,
      isFull
    })
    registrationModal.open()
  }

  const handleCancel = async () => {
    setIsSubmitting(true)
    try {
      const res = await axios.post(`/events/${eventId}/cancel`)
      const status = res.data?.registrationStatus || 'none'
      // Emit before the state update / refetch for the same reason as
      // handleRegistrationSuccess: guarantee the dataLayer push isn't skipped by a
      // downstream synchronous re-render throwing.
      community_event_cancel_success_event({ event: gaEvent })
      setRegistrationStatus(status)
      onRegistrationChange?.(status)
      setToast({ message: 'Your registration has been cancelled.' })
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        error?.response?.message ||
        'Unable to cancel registration. Please try again.'
      setToast({ message, error: true })
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!user) {
    return (
      <Button
        type='button'
        variant='solid'
        onClick={(e) => handleRegisterClick(e)}
        {...buttonProps}
        className='rsvp'
      >
        Sign in to register
      </Button>
    )
  }

  if (isRegistered) {
    return (
      <>
        <ActionGroup $compact={isCompact} $fullWidth={fullWidth}>
          <Button
            variant='solid'
            disabled
            whiteSpace='nowrap'
            {...buttonProps}
            className='eventCancelGroup'
          >
            {STATUS_LABELS[registrationStatus] || 'Registered'}
          </Button>
          {showCancelButton && (
            <Button
              type='button'
              variant='ghost'
              whiteSpace='nowrap'
              onClick={getCancelConfirmation}
              disabled={isSubmitting}
              {...buttonProps}
              className='cancel eventCancelGroup'
            >
              {isSubmitting ? 'Cancelling...' : 'Cancel'}
            </Button>
          )}
        </ActionGroup>
        {openConfirmation && (
          <CancelConfirmationModal
            open={openConfirmation}
            onClose={() => setOpenConfirmation(false)}
            onConfirm={() => {
              handleCancel()
              setOpenConfirmation(false)
            }}
          />
        )}
      </>
    )
  }

  return (
    <>
      <Button
        type='button'
        variant='solid'
        className='rsvp'
        onClick={handleRegisterClick}
        disabled={isSubmitting || disableButton(mainButtonText)}
        {...buttonProps}
      >
        {isSubmitting ? 'Registering...' : mainButtonText}
      </Button>

      {registrationModal.active && (
        <EventRegistrationModal
          key={`${eventId}-registration`}
          useModal={registrationModal}
          eventId={eventId}
          eventTitle={eventTitle}
          registrationQuestions={questions}
          registrationDefaults={registrationDefaults}
          submitLabel={mainButtonText}
          onSuccess={handleRegistrationSuccess}
        />
      )}
    </>
  )
}

EventRegisterButton.propTypes = {
  eventId: PropTypes.string,
  eventTitle: PropTypes.string,
  registrationQuestions: PropTypes.arrayOf(PropTypes.object),
  registrationDefaults: PropTypes.shape({
    nameRequirement: PropTypes.oneOf(['full-name', 'first-last']),
    phoneNumberRequirement: PropTypes.oneOf(['optional', 'required'])
  }),
  registrationStatus: PropTypes.oneOf([
    'none',
    'registered',
    'waitlisted',
    'pending',
    'declined'
  ]),
  isPast: PropTypes.bool,
  size: PropTypes.oneOf(['default', 'compact']),
  fullWidth: PropTypes.bool,
  onRegistrationChange: PropTypes.func
}

export default EventRegisterButton
